import React from 'react'
import { observer, inject } from 'mobx-react'
import { StateType } from '../state/state'
import { kebabCase } from 'lodash-es'
import { ScaleSymLog, ScaleLinear } from 'd3-scale'
import {
  ANIMATION_DURATION_SEC,
  ACTIVE_DOT_RADIUS,
  COUNTRY_LABEL_RECT_HEIGHT,
  XY_LABEL_RECT_HEIGHT,
  UNKNOWN_DATUM_LABEL,
} from '../constants'
import { formatSIWithIntegerThreshold } from '../../../lib/format-utils'
import { AnimatedText } from './AnimatedText'

const VERTICAL_SPACE_BETWEEN_LABELS = 2

interface Props {
  className?: string
  state?: StateType
  xScale: ScaleSymLog<number, number> | ScaleLinear<number, number>
  yScale: ScaleSymLog<number, number> | ScaleLinear<number, number>
}

export const Labels: React.FC<Props> = inject('state')(
  observer(({ className = '', state, xScale, yScale }) => {
    const {
      circles,
      isCountryOvered,
      isCountryActive,
      isIntoDomains,
      xUnitOfMeasure,
      yUnitOfMeasure,
      showXYLabels,
    } = state.ScatterplotState

    return (
      <g className={`-labels ${className}`}>
        {circles.map((datum) => {
          const { x, y, country, country_code, showXNA, showYNA } = datum
          const isOvered = isCountryOvered(country)
          const isActive = isCountryActive(country)
          const cx = xScale(x)
          const cy = yScale(y)
          const xInvert = xScale.invert(cx)
          const yInvert = yScale.invert(cy)
          const isPointIntoDomains = isIntoDomains(xInvert, yInvert)
          const xyOpacity = showXYLabels && isActive ? 1 : 0
          const xLabel = cx + ACTIVE_DOT_RADIUS + 3
          const xValueToShow = showXNA
            ? UNKNOWN_DATUM_LABEL
            : `${formatSIWithIntegerThreshold(x)} ${xUnitOfMeasure}`
          const yValueToShow = showYNA
            ? UNKNOWN_DATUM_LABEL
            : `${formatSIWithIntegerThreshold(y)} ${yUnitOfMeasure}`

          return (
            isPointIntoDomains &&
            (isOvered || isActive) && (
              <g className={`-label-${kebabCase(country)}`} key={kebabCase(country)}>
                <AnimatedText
                  className={`f5 user-select-none pointer-events-none ${className}`}
                  x={xLabel}
                  y={cy - COUNTRY_LABEL_RECT_HEIGHT}
                  yText={
                    cy -
                    COUNTRY_LABEL_RECT_HEIGHT +
                    COUNTRY_LABEL_RECT_HEIGHT / 2 +
                    VERTICAL_SPACE_BETWEEN_LABELS
                  }
                  fill="black"
                  opacity={isOvered || isActive ? 1 : 0}
                  height={COUNTRY_LABEL_RECT_HEIGHT}
                  textAnchor="start"
                  dominantBaseline="middle"
                  animationDurationSec={ANIMATION_DURATION_SEC}
                  style={{ fontWeight: 200 }}
                  animationEnter={{ opacity: 0 }}
                >
                  {country_code}
                </AnimatedText>

                <AnimatedText
                  className={`f7 user-select-none pointer-events-none ${className}`}
                  x={xLabel}
                  y={cy + VERTICAL_SPACE_BETWEEN_LABELS}
                  yText={cy + VERTICAL_SPACE_BETWEEN_LABELS + XY_LABEL_RECT_HEIGHT / 2 - 6}
                  fill="black"
                  opacity={xyOpacity}
                  height={XY_LABEL_RECT_HEIGHT}
                  textAnchor="start"
                  dominantBaseline="middle"
                  animationDurationSec={ANIMATION_DURATION_SEC}
                  style={{ fontWeight: 200 }}
                  animationEnter={{ opacity: 0 }}
                >
                  {yValueToShow}
                  {xValueToShow}
                </AnimatedText>
              </g>
            )
          )
        })}
      </g>
    )
  })
)
